import { useState, useCallback, useEffect } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { 
  Sparkles, 
  Layers, 
  Circle, 
  RotateCw, 
  Plus, 
  Minus, 
  Check,
  ChevronDown
} from 'lucide-react';
import { METALLIC_COLORS } from '@/hooks/useQRContext';

/**
 * Checks if a color has sufficient contrast with another
 */
function getContrastRatio(color1, color2) {
  const getLuminance = (hex) => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = (rgb >> 16 & 255) / 255;
    const g = (rgb >> 8 & 255) / 255;
    const b = (rgb & 255) / 255;
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  };
  
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Gradient stop editor component
 */
function GradientStopEditor({ stops, onChange }) {
  const addStop = () => {
    if (stops.length >= 6) return;
    const newStop = { offset: 0.5, color: '#888888' };
    const newStops = [...stops, newStop].sort((a, b) => a.offset - b.offset);
    onChange(newStops);
  };

  const removeStop = (index) => {
    if (stops.length <= 2) return;
    const newStops = stops.filter((_, i) => i !== index);
    onChange(newStops);
  };

  const updateStop = (index, field, value) => {
    const newStops = stops.map((stop, i) => 
      i === index ? { ...stop, [field]: value } : stop
    );
    if (field === 'offset') {
      newStops.sort((a, b) => a.offset - b.offset);
    }
    onChange(newStops);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Gradient Stops</span>
        <button
          onClick={addStop}
          disabled={stops.length >= 6}
          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      
      {/* Gradient preview bar */}
      <div 
        className="h-6 rounded-lg border border-slate-200 dark:border-slate-600"
        style={{
          background: `linear-gradient(to right, ${stops.map(s => `${s.color} ${s.offset * 100}%`).join(', ')})`
        }}
      />
      
      {/* Stop editors */}
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {stops.map((stop, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="color"
              value={stop.color}
              onChange={(e) => updateStop(index, 'color', e.target.value)}
              className="w-8 h-8 rounded cursor-pointer"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={stop.offset * 100}
              onChange={(e) => updateStop(index, 'offset', parseInt(e.target.value) / 100)}
              className="flex-1"
            />
            <span className="text-xs text-slate-500 w-8">{Math.round(stop.offset * 100)}%</span>
            <button
              onClick={() => removeStop(index)}
              disabled={stops.length <= 2}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50"
            >
              <Minus className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Advanced Color Picker Component
 * Supports solid colors, gradients, and metallic presets
 */
export function AdvancedColorPicker({ 
  value, 
  onChange, 
  onGradientChange,
  gradient = null,
  label = 'Color',
  showContrast = false,
  contrastWith = '#ffffff',
  className = ''
}) {
  const [mode, setMode] = useState(gradient ? 'gradient' : 'solid');
  const [gradientType, setGradientType] = useState(gradient?.type || 'linear');
  const [gradientRotation, setGradientRotation] = useState(gradient?.rotation || 0);
  const [gradientStops, setGradientStops] = useState(
    gradient?.colors || [
      { offset: 0, color: value || '#000000' },
      { offset: 1, color: '#666666' }
    ]
  );
  const [showPicker, setShowPicker] = useState(false);

  // Calculate contrast ratio
  const contrastRatio = showContrast ? getContrastRatio(value || '#000000', contrastWith) : null;
  const contrastLevel = contrastRatio >= 7 ? 'AAA' : contrastRatio >= 4.5 ? 'AA' : contrastRatio >= 3 ? 'AA Large' : 'Poor';

  // Update gradient when stops change
  useEffect(() => {
    if (mode === 'gradient' && onGradientChange) {
      onGradientChange({
        type: gradientType,
        rotation: gradientRotation,
        colors: gradientStops
      });
    }
  }, [mode, gradientType, gradientRotation, gradientStops, onGradientChange]);

  const applyMetallicColor = useCallback((metallicKey) => {
    const metallic = METALLIC_COLORS[metallicKey];
    if (metallic) {
      setMode('gradient');
      setGradientType(metallic.type);
      setGradientRotation(metallic.rotation);
      setGradientStops(metallic.colors);
    }
  }, []);

  const getPreviewStyle = () => {
    if (mode === 'gradient') {
      const gradientFunc = gradientType === 'radial' ? 'radial-gradient' : 'linear-gradient';
      const angleOrShape = gradientType === 'radial' ? 'circle' : `${gradientRotation}deg`;
      return {
        background: `${gradientFunc}(${angleOrShape}, ${gradientStops.map(s => `${s.color} ${s.offset * 100}%`).join(', ')})`
      };
    }
    return { backgroundColor: value };
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
        </label>
      )}
      
      {/* Mode selector */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
        <button
          onClick={() => {
            setMode('solid');
            if (onChange) onChange(value);
          }}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            mode === 'solid' 
              ? 'bg-white dark:bg-slate-700 shadow text-primary-600' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Circle className="w-3.5 h-3.5" />
          Solid
        </button>
        <button
          onClick={() => setMode('gradient')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            mode === 'gradient' 
              ? 'bg-white dark:bg-slate-700 shadow text-primary-600' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          Gradient
        </button>
        <button
          onClick={() => setMode('metallic')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            mode === 'metallic' 
              ? 'bg-white dark:bg-slate-700 shadow text-primary-600' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Metallic
        </button>
      </div>

      {/* Color preview & picker trigger */}
      <div className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-full flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-primary-300 transition-colors"
        >
          <div 
            className="w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-500 shadow-inner"
            style={getPreviewStyle()}
          />
          <div className="flex-1 text-left">
            {mode === 'solid' && (
              <span className="font-mono text-sm text-slate-700 dark:text-slate-300">{value}</span>
            )}
            {mode === 'gradient' && (
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {gradientType === 'linear' ? 'Linear' : 'Radial'} Gradient ({gradientStops.length} stops)
              </span>
            )}
            {mode === 'metallic' && (
              <span className="text-sm text-slate-700 dark:text-slate-300">
                Select a metallic preset
              </span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
        </button>

        {/* Contrast indicator */}
        {showContrast && mode === 'solid' && (
          <div className={`absolute right-12 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-xs font-medium ${
            contrastLevel === 'AAA' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            contrastLevel === 'AA' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {contrastLevel}
          </div>
        )}

        {/* Picker panel */}
        {showPicker && (
          <div className="absolute z-50 top-full left-0 right-0 mt-2 p-4 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700">
            {mode === 'solid' && (
              <div className="space-y-4">
                <HexColorPicker color={value} onChange={onChange} style={{ width: '100%' }} />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">#</span>
                  <HexColorInput 
                    color={value} 
                    onChange={onChange}
                    className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-mono"
                  />
                </div>
                
                {/* Quick colors */}
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Quick Colors</span>
                  <div className="grid grid-cols-8 gap-1">
                    {['#000000', '#374151', '#6B7280', '#9CA3AF', '#EF4444', '#F59E0B', '#22C55E', '#3B82F6', 
                      '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#ffffff', '#F3F4F6', '#E5E7EB', '#D1D5DB'].map(color => (
                      <button
                        key={color}
                        onClick={() => onChange(color)}
                        className={`w-6 h-6 rounded border ${color === value ? 'ring-2 ring-primary-500 ring-offset-1' : 'border-slate-200 dark:border-slate-600'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {mode === 'gradient' && (
              <div className="space-y-4">
                {/* Gradient type */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setGradientType('linear')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      gradientType === 'linear'
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}
                  >
                    Linear
                  </button>
                  <button
                    onClick={() => setGradientType('radial')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      gradientType === 'radial'
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}
                  >
                    Radial
                  </button>
                </div>

                {/* Rotation (for linear) */}
                {gradientType === 'linear' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Angle</span>
                      <span className="text-sm text-slate-500">{gradientRotation} deg</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={gradientRotation}
                        onChange={(e) => setGradientRotation(parseInt(e.target.value))}
                        className="flex-1"
                      />
                      <button
                        onClick={() => setGradientRotation((prev) => (prev + 45) % 360)}
                        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <RotateCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Gradient stops */}
                <GradientStopEditor stops={gradientStops} onChange={setGradientStops} />
              </div>
            )}

            {mode === 'metallic' && (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(METALLIC_COLORS).map(([key, metallic]) => (
                  <button
                    key={key}
                    onClick={() => applyMetallicColor(key)}
                    className="p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-primary-300 transition-all group"
                  >
                    <div 
                      className="w-full h-12 rounded-lg mb-2"
                      style={{
                        background: `linear-gradient(${metallic.rotation}deg, ${metallic.colors.map(c => `${c.color} ${c.offset * 100}%`).join(', ')})`
                      }}
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-primary-600">
                      {metallic.name}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowPicker(false)}
              className="mt-4 w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdvancedColorPicker;
